// src/data/projects.ts
//
// Every project shown on /projects. Strings are stored as LocaleText maps and
// translated at render by the page's tx() helper, so this module is a plain
// constant with no dependency on React or the language context.

import type { ProjectEntry } from "../types";
import { HACKSEOUL_PLACEMENT, STORE_LINKS } from "./site-facts";

export const projects: ProjectEntry[] = [
  {
    id: "stylerx",
    title: {
      en: `StylerX — ${HACKSEOUL_PLACEMENT.label.en}`,
      ko: `StylerX — ${HACKSEOUL_PLACEMENT.label.ko}`,
      uz: `StylerX — ${HACKSEOUL_PLACEMENT.label.uz}`,
    },
    description: {
      en: "AI-powered fashion recommendation and style analysis platform that analyzes your wardrobe and suggests the best outfits. An award-winning project at HackSeoul 2025.",
      ko: "AI 기반 패션 추천 및 스타일 분석 플랫폼으로, 사용자의 옷장을 분석하고 최적의 코디를 제안합니다. HackSeoul 2025에서 수상한 혁신적인 팀 프로젝트입니다.",
      uz: "Sun'iy intellektga asoslangan moda tavsiya va stil tahlil platformasi. Gardrobingizni tahlil qilib, eng yaxshi kiyim kombinatsiyalarini taklif qiladi. HackSeoul 2025 da mukofotlangan loyiha.",
    },
    role: {
      en: "Team Lead & Full-Stack Developer",
      ko: "팀 리드 & 풀스택 개발자",
      uz: "Jamoa Rahbari & Full-Stack Dasturchi",
    },
    year: "2025",
    tech: [
      "Python",
      "FastAPI",
      "Google Gemini AI",
      "React Native",
      "Expo",
      "TypeScript",
      "Router",
      "MongoDB",
      "Firebase",
    ],
    url: "https://github.com/AkmalMakh/StylerX",
    github: "https://github.com/AkmalMakh/StylerX",
    featured: true,
    status: "completed",
    image: "🏆",
  },
  {
    id: "ai-buddy",
    title: {
      en: "AI Buddy Chatbot Project",
      ko: "AI Buddy 챗봇 프로젝트",
      uz: "AI Buddy Chatbot Loyihasi",
    },
    description: {
      en: "AI-powered conversational chatbot web application with real-time messaging capabilities and intelligent response generation.",
      ko: `AI 기반 대화형 챗봇 웹 애플리케이션으로 실시간 메시징 기능과 지능형 응답 생성 기능을 제공합니다.`,
      uz: "Sun'iy intellektga asoslangan suhbat chatbot veb ilovasi. Real vaqtda xabar almashish va aqlli javob yaratish imkoniyatlari.",
    },
    role: {
      en: "Full-Stack Developer",
      ko: "풀스택 개발자",
      uz: "Full-Stack Dasturchi",
    },
    year: "2024",
    tech: ["TypeScript", "React", "AI API", "WebSocket", "CSS3"],
    url: "https://app-aibuddy.calsplatz.com:8000/gen-bi",
    featured: true,
    status: "completed",
    image: "🤖",
  },
  {
    id: "bananatalk",
    title: {
      en: "BananaTalk Language Exchange App",
      ko: "BananaTalk 언어교환 플랫폼",
      uz: "BananaTalk Til Almashish Ilovasi",
    },
    description: {
      en: "Language exchange app connecting people with native speakers across 100+ languages. No flashcards or scripted lessons — just real conversations through instant chat, voice messages, smart translation, and AI study tools. Free to use on iOS and Android.",
      ko: "100개 이상의 언어를 지원하는 언어교환 앱입니다. 원어민과 실시간 채팅, 음성 메시지, 스마트 번역, AI 학습 도구를 통해 실제 대화로 언어를 배울 수 있습니다. iOS와 Android에서 무료로 이용 가능합니다.",
      uz: "100 dan ortiq tilni qo'llab-quvvatlovchi til almashish ilovasi. Ona tilida so'zlashuvchilar bilan real vaqtda suhbat, ovozli xabarlar, aqlli tarjima va AI o'rganish vositalari orqali tillarni o'rganing. iOS va Android'da bepul foydalanish mumkin.",
    },
    role: {
      en: "Owner & Full-Stack Developer",
      ko: "오너 & 풀스택 개발자",
      uz: "Egasi & Full-Stack Dasturchi",
    },
    year: "2024",
    tech: [
      "Flutter",
      "Flutter Riverpod",
      "React",
      "TypeScript",
      "Node.js",
      "MongoDB",
      "Redis",
      "WebRTC",
      "Socket.io",
      "Firebase",
    ],
    url: "https://banatalk.com",
    iosUrl: STORE_LINKS.bananatalk.ios,
    androidUrl: STORE_LINKS.bananatalk.android,
    github: "https://github.com/firdavs9777/language_exchange_web_front",
    featured: true,
    status: "completed",
    image: "🌍",
  },
  {
    id: "tezsell",
    title: {
      en: "TezSell - Neighborhood Marketplace",
      ko: "TezSell - 동네 마켓플레이스",
      uz: "TezSell - Mahalla Bozori",
    },
    description: {
      en: "Neighborhood marketplace app. Buy and sell items, offer services, and list real estate with neighbors. Features direct chat, secure transactions, and is free to use. Available on iOS and Android.",
      ko: "동네 마켓플레이스 앱. 이웃과 물건을 사고팔고, 서비스를 제공하며, 부동산 매물을 등록할 수 있습니다. 직접 채팅, 안전한 거래, 무료로 이용 가능. iOS와 Android에서 다운로드 가능합니다.",
      uz: "Mahalla bozori ilovasi. Qo'shnilaringiz bilan narsalarni sotib oling va soting, xizmatlar taklif qiling va ko'chmas mulk e'lonlarini joylashtiring. To'g'ridan-to'g'ri suhbat, xavfsiz tranzaksiyalar va bepul foydalanish mumkin. iOS va Android'da mavjud.",
    },
    role: {
      en: "Owner & Lead Developer",
      ko: "오너 & 리드 개발자",
      uz: "Egasi & Bosh Dasturchi",
    },
    year: "2024-2025",
    tech: [
      "Flutter",
      "Flutter Riverpod",
      "Django",
      "PostgreSQL",
      "Redis",
      "REST API",
      "Digital Ocean",
      "Nginx",
    ],
    url: "https://webtezsell.com",
    iosUrl: STORE_LINKS.tezsell.ios,
    androidUrl: STORE_LINKS.tezsell.android,
    github: "https://github.com/firdavs9777/tezsell_app_front",
    featured: true,
    status: "in-progress",
    image: "🛒",
  },
  {
    id: "la-international-airport-system",
    title: {
      en: "LA International Airport System",
      ko: "LA 국제공항 시스템",
      uz: "LA Xalqaro Aeroporti Tizimi",
    },
    description: {
      en: "Financial services system development for LA International Airport with loan and insurance application processing.",
      ko: "LA 국제공항 내 금융 서비스 시스템으로 대출 및 보험 신청 처리 기능을 제공합니다.",
      uz: "LA Xalqaro Aeroporti uchun moliyaviy xizmatlar tizimi. Kredit va sug'urta arizalarini qayta ishlash imkoniyatlari.",
    },
    role: {
      en: "Project Manager",
      ko: "프로젝트 매니저",
      uz: "Loyiha Boshqaruvchisi",
    },
    year: "2023",
    tech: ["Vue.js", "TypeScript", "Node.js", "AWS Lambda", "MySQL"],
    featured: true,
    status: "completed",
    image: "✈️",
  },
  {
    id: "insurance-loan-management-system",
    title: {
      en: "Insurance & Loan Management System",
      ko: "보험 및 대출 관리 시스템",
      uz: "Sug'urta va Kredit Boshqaruv Tizimi",
    },
    description: {
      en: "Comprehensive management system for insurance and loan services in Philippines with automated fee calculation.",
      ko: "필리핀 현지 보험 및 대출 서비스를 위한 종합 관리 시스템으로 자동 수수료 계산 기능을 포함합니다.",
      uz: "Filippindagi sug'urta va kredit xizmatlari uchun kompleks boshqaruv tizimi. Avtomatlashtirilgan to'lov hisoblash imkoniyati.",
    },
    role: {
      en: "Project Manager",
      ko: "프로젝트 매니저",
      uz: "Loyiha Boshqaruvchisi",
    },
    year: "2022",
    tech: ["Vue.js", "TypeScript", "Node.js", "MySQL", "SQL Procedures"],
    featured: false,
    status: "completed",
    image: "🏦",
  },
  {
    id: "korean-learning-website",
    title: {
      en: "Korean Learning Website",
      ko: "한국어 학습 웹사이트",
      uz: "Koreys Tilini O'rganish Veb-sayti",
    },
    description: {
      en: "Online Korean language learning platform with course management and video upload capabilities.",
      ko: "온라인 한국어 학습 플랫폼으로 강의 관리 및 동영상 업로드 기능을 제공합니다.",
      uz: "Onlayn koreys tilini o'rganish platformasi. Kurs boshqaruvi va video yuklash imkoniyatlari.",
    },
    role: {
      en: "Frontend Team Lead",
      ko: "프론트엔드 팀 리드",
      uz: "Frontend Jamoa Rahbari",
    },
    year: "2021-2022",
    tech: ["Vue.js", "TypeScript", "Django", "HTML5", "CSS3"],
    featured: false,
    status: "completed",
    image: "📚",
  },
  {
    id: "github-user-search-project",
    title: {
      en: "GitHub User Search Project",
      ko: "GitHub 사용자 검색 프로젝트",
      uz: "GitHub Foydalanuvchi Qidiruv Loyihasi",
    },
    description: {
      en: "Real-time GitHub user profile and repository search application with responsive design.",
      ko: "GitHub 사용자 프로필 및 저장소 실시간 검색 애플리케이션으로 반응형 디자인을 적용했습니다.",
      uz: "GitHub foydalanuvchi profili va repozitoriyalarini real vaqtda qidirish ilovasi. Moslashuvchan dizayn.",
    },
    role: {
      en: "Solo Developer",
      ko: "개인 개발",
      uz: "Yakka Dasturchi",
    },
    year: "2023",
    tech: ["React", "TypeScript", "GitHub API", "CSS3"],
    github: "https://github.com/firdavs9777/github-search",
    featured: false,
    status: "completed",
    image: "🔍",
  },
  {
    id: "coach-finder-platform",
    title: {
      en: "Coach Finder Platform",
      ko: "코치파인더 플랫폼",
      uz: "Coach Finder Platformasi",
    },
    description: {
      en: "User-friendly coaching service booking platform with search and reservation functionality.",
      ko: "사용자 친화적인 코칭 서비스 예약 플랫폼으로 검색 및 예약 기능을 제공합니다.",
      uz: "Foydalanuvchilarga qulay murabbiylik xizmati bron qilish platformasi. Qidiruv va bron qilish funksiyalari.",
    },
    role: {
      en: "Solo Developer",
      ko: "개인 개발",
      uz: "Yakka Dasturchi",
    },
    year: "2022",
    tech: ["Vue.js", "TypeScript", "HTML5", "CSS3", "JavaScript"],
    featured: false,
    status: "completed",
    image: "🎯",
  },
  // NEW PROJECTS ADDED BELOW
  {
    id: "trello-clone-task-management",
    title: {
      en: "Trello Clone - Task Management",
      ko: "Trello 클론 - 작업 관리",
      uz: "Trello Kloni - Vazifalarni Boshqarish",
    },
    description: {
      en: "A modern, responsive task management application with drag-and-drop functionality. Organize projects with boards, lists, and cards.",
      ko: "드래그 앤 드롭 기능이 있는 현대적이고 반응형 작업 관리 애플리케이션입니다. 보드, 리스트, 카드로 프로젝트를 구성할 수 있습니다.",
      uz: "Sudrab tashlash funksiyasiga ega zamonaviy vazifalarni boshqarish ilovasi. Loyihalarni doskalar, ro'yxatlar va kartalar bilan tashkil qiling.",
    },
    role: {
      en: "Solo Developer",
      ko: "개인 개발",
      uz: "Yakka Dasturchi",
    },
    year: "2025",
    tech: ["React", "TypeScript", "Drag & Drop API", "CSS3"],
    github: "https://github.com/firdavs9777/trello_clone",
    featured: false,
    status: "completed",
    image: "📋",
  },
  {
    id: "proshop-ecommerce-website",
    title: {
      en: "ProShop E-commerce Website",
      ko: "ProShop 전자상거래 웹사이트",
      uz: "ProShop Elektron Tijorat Veb-sayti",
    },
    description: {
      en: "Full-stack e-commerce platform with product management, shopping cart, payment processing, and user authentication.",
      ko: "풀스택 전자상거래 플랫폼으로 제품 관리, 장바구니, 결제 처리 및 사용자 인증 기능을 제공합니다.",
      uz: "To'liq stekli elektron tijorat platformasi. Mahsulotlarni boshqarish, savatcha, to'lov qayta ishlash va foydalanuvchi autentifikatsiyasi.",
    },
    role: {
      en: "Full-Stack Developer",
      ko: "풀스택 개발자",
      uz: "Full-Stack Dasturchi",
    },
    year: "2025",
    tech: ["React", "Express.js", "MongoDB", "Redux", "PayPal API"],
    github: "https://github.com/firdavs9777/proshop_website",
    featured: false,
    status: "completed",
    image: "🛍️",
  },
  {
    id: "django-recipe-app-api",
    title: {
      en: "Django Recipe App API",
      ko: "Django Recipe API",
      uz: "Django Retsept Ilovasi API",
    },
    description: {
      en: "Django-based REST API for recipe management with user accounts, recipe CRUD operations, and image upload support.",
      ko: "레시피 관리를 위한 Django 기반 REST API로 사용자 계정, 레시피 CRUD 작업 및 이미지 업로드 기능을 지원합니다.",
      uz: "Retseptlarni boshqarish uchun Django asosidagi REST API. Foydalanuvchi hisoblari, retsept CRUD operatsiyalari va rasm yuklash imkoniyati.",
    },
    role: {
      en: "Backend Developer",
      ko: "백엔드 개발자",
      uz: "Backend Dasturchi",
    },
    year: "2024",
    tech: ["Django", "Django REST Framework", "PostgreSQL", "Docker"],
    github: "https://github.com/firdavs9777/django-recipe-app-api",
    featured: false,
    status: "completed",
    image: "🍳",
  },
  {
    id: "react-investment-calculator",
    title: {
      en: "React Investment Calculator",
      ko: "React 투자 계산기",
      uz: "React Investitsiya Kalkulyatori",
    },
    description: {
      en: "A simple and intuitive investment calculator to estimate future value of investments based on initial investment, interest rate, and duration.",
      ko: "초기 투자금, 이자율, 기간을 기반으로 투자의 미래 가치를 추정할 수 있는 간단하고 직관적인 투자 계산기입니다.",
      uz: "Boshlang'ich investitsiya, foiz stavkasi va muddat asosida investitsiyalarning kelajakdagi qiymatini hisoblash uchun oddiy va qulay kalkulyator.",
    },
    role: {
      en: "Solo Developer",
      ko: "개인 개발",
      uz: "Yakka Dasturchi",
    },
    year: "2024",
    tech: ["React", "JavaScript", "CSS3", "Financial Algorithms"],
    github: "https://github.com/firdavs9777/react_investment_calcutor",
    featured: false,
    status: "completed",
    image: "💰",
  },
  {
    id: "django-btre-real-estate-project",
    title: {
      en: "Django BTRE Real Estate Project",
      ko: "Django BTRE 부동산 프로젝트",
      uz: "Django BTRE Ko'chmas Mulk Loyihasi",
    },
    description: {
      en: "Django-based real estate website with property listings, search functionality, and inquiry management.",
      ko: "부동산 매물 관리, 검색 및 문의 기능을 갖춘 Django 기반 부동산 웹사이트입니다.",
      uz: "Django asosidagi ko'chmas mulk veb-sayti. Mulk ro'yxatlari, qidiruv funksiyasi va so'rovlarni boshqarish.",
    },
    role: {
      en: "Full-Stack Developer",
      ko: "풀스택 개발자",
      uz: "Full-Stack Dasturchi",
    },
    year: "2024",
    tech: ["Django", "Python", "PostgreSQL", "CSS3", "JavaScript"],
    github: "https://github.com/firdavs9777/django_btre_project",
    featured: false,
    status: "completed",
    image: "🏠",
  },
  {
    id: "react-native-goals-app",
    title: {
      en: "React Native Goals App",
      ko: "React Native 목표 추적 앱",
      uz: "React Native Maqsadlar Ilovasi",
    },
    description: {
      en: "Cross-platform mobile app built with React Native and TypeScript for setting and tracking personal goals.",
      ko: "TypeScript가 통합된 크로스 플랫폼 모바일 앱으로 개인 목표를 설정하고 추적할 수 있습니다.",
      uz: "React Native va TypeScript yordamida yaratilgan kross-platformali mobil ilova. Shaxsiy maqsadlarni belgilash va kuzatish.",
    },
    role: {
      en: "Mobile Developer",
      ko: "모바일 개발자",
      uz: "Mobil Dasturchi",
    },
    year: "2024",
    tech: ["React Native", "TypeScript", "JavaScript", "Mobile UI"],
    github: "https://github.com/firdavs9777/goals_app_react_native",
    featured: false,
    status: "completed",
    image: "🎯",
  },
  {
    id: "redux-toolkit-practice",
    title: {
      en: "Redux Toolkit Practice",
      ko: "Redux Toolkit 실습 프로젝트",
      uz: "Redux Toolkit Amaliyot Loyihasi",
    },
    description: {
      en: "Streamlined setup for integrating Redux Toolkit into React projects, excellent practice for learners and state management.",
      ko: "Redux Toolkit을 React 프로젝트에 통합하는 간소화된 설정으로 초보자와 학습자를 위한 좋은 연습 프로젝트입니다.",
      uz: "Redux Toolkit'ni React loyihalariga integratsiya qilish uchun soddalashtirilgan sozlash. O'rganuvchilar va holat boshqaruvi uchun ajoyib amaliyot.",
    },
    role: {
      en: "Solo Developer",
      ko: "개인 개발",
      uz: "Yakka Dasturchi",
    },
    year: "2024",
    tech: ["React", "Redux Toolkit", "JavaScript", "State Management"],
    github: "https://github.com/firdavs9777/react_redux_toolkit_practice",
    featured: false,
    status: "completed",
    image: "⚛️",
  },
  {
    id: "react-google-search-location",
    title: {
      en: "React Google Search Location",
      ko: "React Google 위치 검색",
      uz: "React Google Joylashuv Qidiruvi",
    },
    description: {
      en: "Location search application integrating Google Maps API with real-time place search and map display functionality.",
      ko: "Google Maps API를 통합한 위치 검색 애플리케이션으로 실시간 장소 검색 및 지도 표시 기능을 제공합니다.",
      uz: "Google Maps API integratsiyalangan joylashuv qidiruv ilovasi. Real vaqtda joy qidirish va xarita ko'rsatish funksiyalari.",
    },
    role: {
      en: "Frontend Developer",
      ko: "프론트엔드 개발자",
      uz: "Frontend Dasturchi",
    },
    year: "2024",
    tech: ["React", "TypeScript", "Google Maps API", "Geolocation"],
    github: "https://github.com/firdavs9777/React_Google_Search_Location",
    featured: false,
    status: "completed",
    image: "📍",
  },
  {
    id: "django-backend-application",
    title: {
      en: "Django Backend Application",
      ko: "Django 백엔드 애플리케이션",
      uz: "Django Backend Ilovasi",
    },
    description: {
      en: "Django backend application with Docker setup, featuring REST API endpoints and database management capabilities.",
      ko: "Docker로 구성된 Django 백엔드 애플리케이션으로 REST API 엔드포인트와 데이터베이스 관리 기능을 제공합니다.",
      uz: "Docker sozlamalari bilan Django backend ilovasi. REST API endpointlari va ma'lumotlar bazasini boshqarish imkoniyatlari.",
    },
    role: {
      en: "Backend Developer",
      ko: "백엔드 개발자",
      uz: "Backend Dasturchi",
    },
    year: "2024",
    tech: ["Django", "Python", "Docker", "PostgreSQL", "REST API"],
    github: "https://github.com/firdavs9777/django_backend_application",
    featured: false,
    status: "completed",
    image: "🐍",
  },
  {
    id: "spring-boot-backend-application",
    title: {
      en: "Spring Boot Backend Application",
      ko: "Spring Boot 백엔드 애플리케이션",
      uz: "Spring Boot Backend Ilovasi",
    },
    description: {
      en: "Spring Boot backend application with PostgreSQL, featuring REST API endpoints and database management capabilities.",
      ko: "Spring Boot와 PostgreSQL을 사용한 백엔드 애플리케이션으로 REST API 엔드포인트와 데이터베이스 관리 기능을 제공합니다.",
      uz: "PostgreSQL bilan Spring Boot backend ilovasi. REST API endpointlari va ma'lumotlar bazasini boshqarish imkoniyatlari.",
    },
    role: {
      en: "Backend Developer",
      ko: "백엔드 개발자",
      uz: "Backend Dasturchi",
    },
    year: "2025",
    tech: ["Java", "Spring Boot", "PostgreSQL", "REST API", "Hibernate"],
    github: "https://github.com/firdavs9777/spring_boot_backend",
    featured: true,
    status: "in progress",
    image: "☕️",
  },
];
